import React from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import type { DeleteShippingPolicyModalProps } from './types';

const DeleteShippingPolicyModal: React.FC<DeleteShippingPolicyModalProps> = ({
  show,
  onHide,
  onConfirm,
  policy,
  isLoading,
}) => {
  if (!policy) {
    return null;
  }

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Delete Shipping Policy</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant="warning">
          <Alert.Heading>Are you sure?</Alert.Heading>
          <p>
            You are about to delete the shipping policy for <strong>{policy.countryCode}</strong>.
          </p>

          <hr />

          <div className="mb-2">
            <strong>Policy Details:</strong>
          </div>
          <ul className="mb-0">
            <li>
              <strong>Country:</strong> {policy.countryCode}
            </li>
            <li>
              <strong>Shipping Cost:</strong> {formatCurrency(policy.shippingCost)}
            </li>
            <li>
              <strong>Free Shipping Threshold:</strong>{' '}
              {policy.freeShippingThreshold ? formatCurrency(policy.freeShippingThreshold) : 'No free shipping'}
            </li>
            <li>
              <strong>Delivery Time:</strong>{' '}
              {policy.deliveryDuration.minDays === policy.deliveryDuration.maxDays
                ? `${policy.deliveryDuration.minDays} day${policy.deliveryDuration.minDays === 1 ? '' : 's'}`
                : `${policy.deliveryDuration.minDays}-${policy.deliveryDuration.maxDays} days`}
            </li>
            <li>
              <strong>Status:</strong> {policy.enabled ? 'Active' : 'Inactive'}
            </li>
          </ul>
        </Alert>

        <p className="text-danger mb-0">
          <strong>This action cannot be undone.</strong> Customers from this country will no longer have shipping
          options available until you create a new policy.
        </p>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="danger" onClick={onConfirm} disabled={isLoading}>
          {isLoading ? 'Deleting...' : 'Delete Policy'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteShippingPolicyModal;
